// Pure vote arithmetic + cache-page walking for the optimistic vote
// UI (issue #241). Lives outside the hook so it imports neither React
// Query nor the API client -- unit-testable with zero mocks.
export type VoteDirection = 'up' | 'down';

type Votable = { id: string; score: number; viewerVote?: 'up' | 'down' };

// The vote arithmetic: a re-vote first retracts the old direction, then
// applies the new one; a re-tap of the active direction is a removal.
export function applyVoteChange(item: Votable, direction: VoteDirection | undefined): Votable {
  const previous = item.viewerVote;
  let score = item.score;

  if (previous !== undefined) {
    score += previous === 'up' ? -1 : 1;
  }

  if (direction !== undefined) {
    score += direction === 'up' ? 1 : -1;
  }

  return { ...item, score, viewerVote: direction };
}

// Walks every page of an infinite-query cache shape, patching only the
// matching id. Threads pages carry a `threads` array; posts pages carry
// `items`.
export function applyVoteChangeToPages<T extends Votable>(
  pages: { [key: string]: unknown }[],
  id: string,
  direction: VoteDirection | undefined,
): { [key: string]: unknown }[] {
  return pages.map((page) => {
    if (Array.isArray(page.threads)) {
      return {
        ...page,
        threads: (page.threads as T[]).map((item) => (item.id === id ? applyVoteChange(item, direction) : item)),
      };
    }

    if (Array.isArray(page.items)) {
      return {
        ...page,
        items: (page.items as T[]).map((item) => (item.id === id ? applyVoteChange(item, direction) : item)),
      };
    }

    return page;
  });
}
